import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, ShieldQuestion, UserX } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { createDateColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { newPasswordPolicyError, newPasswordRequirementsHint } from '../lib/passwordPolicy';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  roles: Array<{
    key: string;
    name: string;
    description: string;
  }>;
  is_active?: boolean;
}

interface Role {
  key: string;
  name: string;
  description: string;
}

interface FormData {
  email?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  password?: string;
  role_keys?: string[];
}

export default function Users() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [form, setForm] = useState<FormData>({});
  const navigate = useNavigate();

  useEffect(() => {
    // Check for authentication
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (!token || !savedUser) {
      navigate('/login');
      return;
    }

    try {
      setUser(JSON.parse(savedUser));
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
      return;
    }

    setLoading(false);
    load();
    loadRoles();
  }, [navigate]);

  useEffect(() => {
    load();
  }, [page, limit, debouncedSearch]);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await fetch(`/api/users?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.users || data.data || []);
      setRows(list);
      const p = (data.pagination || {});
      if (p.total != null) setTotal(p.total);
      if (p.pages != null) setPages(p.pages);
    } catch (e) {
      console.error('Failed to load users', e);
    }
  };

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/roles', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const rolesList = Array.isArray(data) ? data : (data.roles || data.data || []);
      setAvailableRoles(rolesList);
    } catch (e) {
      console.error('Failed to load roles', e);
      // Fallback to hardcoded roles if API fails
      setAvailableRoles([
        { key: 'operator', name: 'Operator', description: 'Basic access' },
        { key: 'supervisor', name: 'Supervisor', description: 'Supervisory access' },
        { key: 'admin', name: 'Administrator', description: 'Full system access' },
        { key: 'driver', name: 'Driver', description: 'Delivery driver' },
        { key: 'dispatcher', name: 'Dispatcher', description: 'Dispatch coordinator' }
      ]);
    }
  };

  const startEdit = (u?: User) => {
    const target = u || null;
    setEditing(target);
    setForm(target ? {
      email: target.email,
      first_name: target.first_name,
      last_name: target.last_name,
      phone_number: target.phone_number || '',
      role_keys: target.roles?.map(r => r.key) || ['operator']
    } : {
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      password: '',
      role_keys: ['operator']
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm({});
  };

  const save = async () => {
    if (!form.email || !form.first_name || !form.last_name) {
      window.alert('Email, first and last name are required');
      return;
    }

    const isEdit = !!editing?.id;

    if (!isEdit) {
      const pwdErr = newPasswordPolicyError(form.password);
      if (pwdErr) {
        window.alert(pwdErr);
        return;
      }
    }

    // Role selection is required
    if (!form.role_keys || form.role_keys.length === 0) {
      window.alert('At least one role must be selected');
      return;
    }

    const token = localStorage.getItem('token');
    const url = isEdit ? `/api/users/${editing!.id}` : '/api/users';
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();

      if (!res.ok) {
        // Show detailed error message if available
        const errorMsg = data.details ?
          data.details.map((err: any) => `${err.field}: ${err.message}`).join('\n') :
          (data.error || data.message || 'Save failed');
        window.alert(errorMsg);
        return;
      }

      closeDialog();
      await load();
    } catch (error) {
      console.error('Error saving user:', error);
      window.alert('Network error occurred. Please try again.');
    }
  };

  const resetPassword = async (u: User) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/users/${u.id}/password-reset`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const data = await res.json();
      window.alert(data.message || 'Password reset email sent');
    } else {
      const errorData = await res.json().catch(() => ({}));
      window.alert(errorData.error || 'Failed to trigger reset');
    }
  };

  const deactivateUser = async (u: User) => {
    if (!window.confirm(`Deactivate ${u.email}?`)) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/users/${u.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ is_active: false }) });
    if (res.ok) await load(); else window.alert('Failed to deactivate user');
  };

  const columns: ColumnDef<User>[] = useMemo(() => ([
    { accessorKey: 'email', header: 'Email' },
    { accessorKey: 'first_name', header: 'First Name' },
    { accessorKey: 'last_name', header: 'Last Name' },
    { accessorKey: 'roles', header: 'Roles', cell: ({ row }) => (row.original.roles || []).map(r => r.name).join(', ') || '-' },
    { accessorKey: 'is_active', header: 'Status', cell: ({ row }) => row.original.is_active === false ? 'Inactive' : 'Active' },
    createDateColumn<User>('created_at', 'Created At', 'datetime'),
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => startEdit(row.original)}><Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={() => resetPassword(row.original)}><ShieldQuestion className="h-4 w-4 mr-2" />Trigger Password Reset</DropdownMenuItem>
            <DropdownMenuItem className="text-red-600" onClick={() => deactivateUser(row.original)}><UserX className="h-4 w-4 mr-2" />Deactivate User</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ]), []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-xl font-heading text-primary">Loading Users...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="users" />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary leading-tight mb-1">Users</h1>
            <p className="text-muted-foreground">Manage user accounts and roles</p>
          </div>
          <Button onClick={() => startEdit(undefined)}>Add User</Button>
        </div>

        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          searchKey="email"
          searchPlaceholder="Search users..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          serverSide={true}
          pagination={{ total, page, limit, pages }}
          onPaginationChange={(newPage, newLimit) => {
            if (newLimit !== limit) { setLimit(newLimit); setPage(1); } else { setPage(newPage); }
          }}
          emptyMessage="No users found."
        />

        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Edit user information and roles. Use "Trigger Password Reset" from the table for password changes.' : 'Create a new user account with roles and password.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email || ''}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="user@homebound.co.uk"
                />
              </div>
              <div>
                <Label>First Name</Label>
                <Input
                  value={form.first_name || ''}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  placeholder="John"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={form.last_name || ''}
                  onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  placeholder="Doe"
                />
              </div>
              <div>
                <Label>Phone Number (Optional)</Label>
                <Input
                  type="tel"
                  value={form.phone_number || ''}
                  onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
                  placeholder="+44 20 1234 5678"
                />
              </div>
              {!editing && (
                <div className="md:col-span-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={form.password || ''}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder={newPasswordRequirementsHint}
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <Label>Roles</Label>
                <div className="mt-2 space-y-2">
                  {availableRoles.map((role) => (
                    <div key={role.key} className="flex items-center space-x-2">
                      <Checkbox
                        id={role.key}
                        checked={form.role_keys?.includes(role.key) || false}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setForm(f => ({
                              ...f,
                              role_keys: [...(f.role_keys || []), role.key]
                            }));
                          } else {
                            setForm(f => ({
                              ...f,
                              role_keys: (f.role_keys || []).filter(k => k !== role.key)
                            }));
                          }
                        }}
                      />
                      <Label htmlFor={role.key} className="text-sm font-normal">
                        <span className="font-medium">{role.name}</span>
                        <span className="text-gray-500 ml-2">{role.description}</span>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="secondary" onClick={closeDialog}>Cancel</Button>
              <Button onClick={save}>{editing ? 'Save Changes' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
